// Pure data + logic for the guided tour of the vehicle module (see
// docs/superpowers/specs/2026-09-01-recorrido-guiado-vehiculos-design.md).
//
// Nothing in this file touches the DOM, React, or the network — it only
// describes the tour's script (§5) and computes *what* the driver should do
// to move between two steps (§3's "conductor"). The actual clicking/
// navigating lives in vehicle-tour.provider.tsx, which is a thin, mostly
// untested shell around these functions. Keeping the decision logic here,
// pure, is what makes it testable per §7.
import {
  ChecklistPrefillSource,
  defaultAccesorios,
  defaultCarroceria,
  defaultLlantas,
} from './vehicleEvidenceSelectors';

// ---------------------------------------------------------------------------
// The simulated loan (§2)
// ---------------------------------------------------------------------------

// A sentinel id/folio, obviously fake, that never collides with a real
// backend record — VehicleFolioDetail and MyVehicleHistory key off this to
// know a route/row belongs to the tour rather than to a real fetch.
export const TOUR_LOAN_ID = 'tour-demo-recorrido';
export const TOUR_FOLIO = 'DEMO-1027';
const TOUR_PREV_FOLIO = 'DEMO-0950';

const TOUR_VEHICULO = {
  marca: 'Nissan',
  modelo: 'NP300',
  placas: 'DEMO-123',
  color: 'Blanco',
  anio: 2022,
};

export interface SimulatedLoan {
  id: string;
  folio: string;
  estado: 'ASIGNADO' | 'ACTIVO';
  vehiculo: typeof TOUR_VEHICULO;
  fechaAsignacion: string;
  empleado: { name: string };
  estadoPrevio: ChecklistPrefillSource | null;
  checkout: {
    fecha: string;
    kilometraje: number;
    combustible: string;
    accesorios: Record<string, boolean>;
    carroceria: Record<string, string>;
    llantas: Record<string, string>;
  } | null;
  checkin: null;
}

export type TourPhase = 'pending' | 'post-checkout';

// Before the (simulated) checkout: the card shows "Pendiente de aceptación"
// and the wizard's "Estado del vehículo" step prefills from a previous
// checkin, exactly like a real vehicle with history would (§2.3/§5.3).
const daysAgo = (n: number): string => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString();
};

// After the (simulated, never-submitted) checkout: the card flips to "En
// uso" so §5.7 (Reportar incidencia, only visible while ACTIVO) has
// something real to point at.
export const buildSimulatedLoan = (phase: TourPhase): SimulatedLoan => {
  const base = {
    id: TOUR_LOAN_ID,
    folio: TOUR_FOLIO,
    vehiculo: TOUR_VEHICULO,
    fechaAsignacion: daysAgo(0),
    empleado: { name: 'Recorrido guiado (ejemplo)' },
  };
  if (phase === 'pending') {
    return {
      ...base,
      estado: 'ASIGNADO',
      estadoPrevio: {
        accesorios: defaultAccesorios(),
        carroceria: defaultCarroceria(),
        origen: 'checkin',
        fecha: daysAgo(4),
        folio: TOUR_PREV_FOLIO,
      },
      checkout: null,
      checkin: null,
    };
  }
  return {
    ...base,
    estado: 'ACTIVO',
    estadoPrevio: null,
    checkout: {
      fecha: daysAgo(0),
      kilometraje: 48210,
      combustible: '3/4',
      accesorios: defaultAccesorios(),
      carroceria: defaultCarroceria(),
      llantas: defaultLlantas(),
    },
    checkin: null,
  };
};

// ---------------------------------------------------------------------------
// data-tour-id targets
// ---------------------------------------------------------------------------

export const TOUR_TARGETS = {
  card: 'tour-vehicle-card',
  actionButton: 'tour-action-button',
  wizardLineamientos: 'tour-wizard-lineamientos',
  wizardDatosViaje: 'tour-wizard-datos-viaje',
  wizardEstado: 'tour-wizard-estado',
  wizardDiagram: 'tour-wizard-diagram',
  wizardFirma: 'tour-wizard-firma',
  wizardNext: 'tour-wizard-next',
  wizardClose: 'tour-wizard-close',
  wizardDone: 'tour-wizard-done',
  success: 'tour-success-screen',
  incidenciaButton: 'tour-incidencia-button',
  tabActual: 'tour-tab-actual',
  tabHistorial: 'tour-tab-historial',
  historyRow: 'tour-history-row',
  folioDetail: 'tour-folio-detail',
} as const;

export const selectorFor = (id: string): string => `[data-tour-id="${id}"]`;

// ---------------------------------------------------------------------------
// The script (§5)
// ---------------------------------------------------------------------------

export type WizardScreen = 'lineamientos' | 'datos-viaje' | 'estado' | 'fotos-firma' | 'success' | null;

export const WIZARD_SCREEN_ORDER: Exclude<WizardScreen, null>[] = [
  'lineamientos',
  'datos-viaje',
  'estado',
  'fotos-firma',
  'success',
];

export interface TourStepPlan {
  tab: 'actual' | 'historial';
  route: 'index' | 'folio';
  phase: TourPhase;
  wizardScreen: WizardScreen;
}

export interface TourStepDef {
  key: string;
  target: string; // data-tour-id
  title: string;
  description: string;
  plan: TourStepPlan;
}

const PENDING_BASE: TourStepPlan = { tab: 'actual', route: 'index', phase: 'pending', wizardScreen: null };
const ACTIVE_BASE: TourStepPlan = { tab: 'actual', route: 'index', phase: 'post-checkout', wizardScreen: null };

export const TOUR_STEPS: TourStepDef[] = [
  {
    key: 'card',
    target: TOUR_TARGETS.card,
    title: 'Tu vehículo asignado',
    description:
      'El estado te dice qué sigue: "Pendiente" significa que debes registrar la salida antes de usarlo; ' +
      '"En uso" es que ya lo tienes y puedes reportar una incidencia o devolverlo; "Por revisar" es que ya lo ' +
      'devolviste y un administrador está confirmando la recepción.',
    plan: PENDING_BASE,
  },
  {
    key: 'action-button',
    target: TOUR_TARGETS.actionButton,
    title: 'Botón principal',
    description:
      'Cambia de "Registrar salida" a "Registrar devolución" según el estado, para que no puedas activar el ' +
      'flujo equivocado por error.',
    plan: PENDING_BASE,
  },
  {
    key: 'wizard-lineamientos',
    target: TOUR_TARGETS.wizardLineamientos,
    title: 'Lineamientos',
    description:
      'Es un compromiso explícito: queda registrado que aceptaste las condiciones de uso antes de poder avanzar, ' +
      'no solo que las viste.',
    plan: { ...PENDING_BASE, wizardScreen: 'lineamientos' },
  },
  {
    key: 'wizard-datos-viaje',
    target: TOUR_TARGETS.wizardDatosViaje,
    title: 'Datos del viaje',
    description:
      'Para qué se usa el vehículo y en qué proyecto — así se puede revisar el uso después sin tener que ' +
      'preguntarle a nadie.',
    plan: { ...PENDING_BASE, wizardScreen: 'datos-viaje' },
  },
  {
    key: 'wizard-estado',
    target: TOUR_TARGETS.wizardEstado,
    title: 'Estado del vehículo',
    description:
      'El kilometraje de salida se compara después contra el de entrada, por eso no se puede devolver con menos. ' +
      'Los checks de accesorios, carrocería y llantas se prellenan con el último estado registrado — aquí se ve ' +
      'de dónde viene, precisamente para que lo revises y no lo repitas a ciegas.',
    plan: { ...PENDING_BASE, wizardScreen: 'estado' },
  },
  {
    key: 'wizard-diagram',
    target: TOUR_TARGETS.wizardDiagram,
    title: 'Diagrama del vehículo',
    description:
      'Cada ángulo tiene su propio punto marcado sobre el auto, así ninguna foto se puede omitir ni confundir ' +
      'con otra al revisar la evidencia.',
    plan: { ...PENDING_BASE, wizardScreen: 'fotos-firma' },
  },
  {
    key: 'wizard-firma',
    target: TOUR_TARGETS.wizardFirma,
    title: 'Firma',
    description:
      'Dibujada o escrita, tienen la misma validez — es la constancia de que quien firma es quien realmente ' +
      'retira el vehículo.',
    plan: { ...PENDING_BASE, wizardScreen: 'fotos-firma' },
  },
  {
    key: 'success',
    target: TOUR_TARGETS.success,
    title: 'Folio de comprobante',
    description:
      'El folio queda como comprobante de la operación — es la referencia que vas a usar para encontrarla ' +
      'después en el historial.',
    plan: { ...PENDING_BASE, wizardScreen: 'success' },
  },
  {
    key: 'incidencia',
    target: TOUR_TARGETS.incidenciaButton,
    title: 'Reportar incidencia',
    description:
      'Aparece solo mientras el vehículo está en uso — sirve para avisar de una falla en el momento, sin tener ' +
      'que esperar hasta la devolución.',
    plan: ACTIVE_BASE,
  },
  {
    key: 'history',
    target: TOUR_TARGETS.historyRow,
    title: 'Historial',
    description:
      'Cada préstamo pasado queda aquí con su folio — puedes consultar una salida anterior sin tener que ' +
      'pedírsela a nadie.',
    plan: { ...ACTIVE_BASE, tab: 'historial' },
  },
  {
    key: 'folio-detail',
    target: TOUR_TARGETS.folioDetail,
    title: 'Detalle del folio',
    description:
      'Aquí se ve exactamente lo que se envió en la salida y, cuando exista, en la devolución — útil para ' +
      'resolver dudas o disputas sobre un préstamo.',
    plan: { ...ACTIVE_BASE, tab: 'historial', route: 'folio' },
  },
];

// ---------------------------------------------------------------------------
// Advance logic: what should the driver do to go from one step's plan to
// another's? (§3 — "un conductor avanza el estado de la app entre pasos".)
// ---------------------------------------------------------------------------

export type TourAction =
  | { type: 'goToIndexRoute' }
  | { type: 'setPhase'; phase: TourPhase }
  | { type: 'setTab'; tab: 'actual' | 'historial' }
  | { type: 'openWizard' }
  | { type: 'wizardNext' }
  | { type: 'closeWizard' }
  | { type: 'finishWizardScreen' }
  | { type: 'openFolioDetail' };

// prevPlan is null when nothing is known yet about the app's current state
// (tour start, or right after a hard reset like leaving the folio route) —
// every field of nextPlan is then (re)applied unconditionally.
export function computeTourActions(prevPlan: TourStepPlan | null, nextPlan: TourStepPlan): TourAction[] {
  const actions: TourAction[] = [];
  let prev = prevPlan;

  // Route: the tab bar and the wizard don't even exist on the folio-detail
  // page (it's a separate route), so leaving it always comes first and
  // invalidates everything else we thought we knew.
  const cameFromFolio = prev?.route === 'folio';
  const needsFolio = nextPlan.route === 'folio';
  if (cameFromFolio && !needsFolio) {
    actions.push({ type: 'goToIndexRoute' });
    prev = null;
  }

  // Phase: switching the simulated loan's phase invalidates whatever the
  // wizard is showing (its prefill/demo values are read from the loan at
  // the moment it opens), so close it first if needed.
  if (!prev || prev.phase !== nextPlan.phase) {
    if (prev && prev.wizardScreen !== null) {
      actions.push(prev.wizardScreen === 'success' ? { type: 'finishWizardScreen' } : { type: 'closeWizard' });
      prev = { ...prev, wizardScreen: null };
    }
    actions.push({ type: 'setPhase', phase: nextPlan.phase });
    prev = prev ? { ...prev, phase: nextPlan.phase } : null;
  }

  // Tab.
  if (!prev || prev.tab !== nextPlan.tab) {
    actions.push({ type: 'setTab', tab: nextPlan.tab });
    prev = prev ? { ...prev, tab: nextPlan.tab } : null;
  }

  // Wizard screen.
  const prevScreen = prev?.wizardScreen ?? null;
  const nextScreen = nextPlan.wizardScreen;
  if (prevScreen !== nextScreen) {
    if (nextScreen === null) {
      actions.push(prevScreen === 'success' ? { type: 'finishWizardScreen' } : { type: 'closeWizard' });
    } else {
      const prevIndex = prevScreen ? WIZARD_SCREEN_ORDER.indexOf(prevScreen) : -1;
      const nextIndex = WIZARD_SCREEN_ORDER.indexOf(nextScreen);
      if (prevIndex === -1) {
        actions.push({ type: 'openWizard' });
        for (let i = 0; i < nextIndex; i += 1) actions.push({ type: 'wizardNext' });
      } else if (nextIndex > prevIndex) {
        for (let i = prevIndex; i < nextIndex; i += 1) actions.push({ type: 'wizardNext' });
      } else {
        // Going backward inside the wizard: there's no "undo" affordance for
        // an already-advanced step, so close and replay forward instead.
        actions.push(prevScreen === 'success' ? { type: 'finishWizardScreen' } : { type: 'closeWizard' });
        actions.push({ type: 'openWizard' });
        for (let i = 0; i < nextIndex; i += 1) actions.push({ type: 'wizardNext' });
      }
    }
  }

  // Route: entering the folio-detail page.
  if (needsFolio && (!cameFromFolio || prev?.route !== 'folio')) {
    actions.push({ type: 'openFolioDetail' });
  }

  return actions;
}
